/**
 * Every tunable number the duel has, in one module.
 *
 * ## Provisional, and saying so
 *
 * These numbers were chosen so a duel lasts a readable length of time and so
 * the first arena can be walked across while being shot at. They are NOT a
 * balance decision - nobody has played this yet. They are versioned
 * (`PVP_RULES_VERSION`, in `@kenocyber/shared`) and centralised here precisely
 * so changing them is one edit and one version bump rather than a hunt.
 *
 * ## The units, once
 *
 * Distance is **milli-units** (mu): 1 000 mu is one arena unit, and an arena
 * unit is what the renderer draws as a pixel at zoom 1. Integers throughout -
 * there is no float in this simulation, so there is no drift, no accumulation
 * error and no engine-dependent last bit.
 *
 * Time is **ticks**. A tick is {@link PVP_TICK_MS} of simulated time and never
 * a frame: the room advances by whole ticks out of an accumulator, so a slow
 * timer produces a late tick and never a longer one.
 *
 * ## Both ships are identical, deliberately
 *
 * This slice normalises attributes: same health, same speed, same cadence, same
 * damage. A duel that reads a player's equipment is a duel decided before it
 * starts, and proving the ARCHITECTURE is what this delivery is for. Loading a
 * frozen per-player profile is a later decision, and the snapshot already has
 * the shape for it.
 */

import { PVP_RUNE_WIRE_KINDS, type PvpRuneWireKind } from '@kenocyber/shared';
import {
  MISSILE_HITBOX_BPS,
  MISSILE_LIFETIME_MS,
  MISSILE_SIZE_BPS,
  MISSILE_SPEED_BPS,
} from '../damage/missile';
import { POISON_TICK_INTERVAL_MS } from '../damage/poison';
import { SHIP_BASE_MAX_HEALTH, SHIP_BASE_MAX_HEALTH_MILLI } from '../hull/base-health';
import { VECTOR_SCALE } from './vectors';

/* -------------------------------------------------------------------------- */
/*  Time                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Simulated milliseconds per tick: 20 Hz.
 *
 * ## Why 20, measured rather than assumed
 *
 * The room's timer was measured against real workerd before this number was
 * chosen. A `setInterval(50)` inside a Durable Object delivered **16.1-16.3 Hz**
 * (about 61.7 ms), consistently enough across eight simultaneous rooms to be a
 * systematic offset rather than jitter. Command round trips over the same
 * transport were 0.94 ms at the median and 8.77 ms at the 99th percentile, so
 * the transport is nowhere near the constraint - the TIMER is.
 *
 * That is the whole argument for an accumulator: the tick rate is a property of
 * the simulation, not of whatever the runtime's scheduler felt like doing. The
 * room asks its timer for {@link PVP_TIMER_INTERVAL_MS} and then advances by
 * whole 50 ms steps out of the wall clock, so a timer that fires late produces
 * two steps rather than one long one, and the duel runs at 20 Hz on a runtime
 * that cannot deliver 20 Hz callbacks.
 */
export const PVP_TICK_MS = 50;

/**
 * What the room asks its timer for.
 *
 * Deliberately SHORTER than a tick. Measured behaviour is that workerd
 * overshoots a requested interval by roughly a quarter, so asking for 25 ms
 * lands near 31 ms and feeds the accumulator about twice per tick. Asking for
 * exactly 50 ms would land near 62 ms and make every tick arrive late by
 * construction.
 */
export const PVP_TIMER_INTERVAL_MS = 25;

/**
 * The most simulated time one timer callback may advance.
 *
 * A room whose runtime stalled for a minute must not resolve a minute of combat
 * in one burst - it must catch up over the next few callbacks. This is the same
 * reasoning as the hunt's 250 ms frame clamp: a gap in wall-clock time is not
 * permission to simulate through it.
 */
export const PVP_MAX_CATCHUP_MS = 250;

/** Two minutes. Reaching it is a draw, never a win on points. */
export const PVP_TIME_LIMIT_TICKS = 20 * 120;

/* -------------------------------------------------------------------------- */
/*  The arena                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **Cinco vezes por cinco vezes: 24 000 x 15 000 units, 25x a área** (gate
 * V28H.K).
 *
 * ## Porque cresceu
 *
 * O contrato do catálogo (gate V28H.B) registou que a arena tem de crescer
 * antes do Catolé e do Leviatã, e que o crescimento é 5x por 5x. As duas cartas
 * não cabem aqui de outra maneira: o Catolé leva literalmente **6x** o raio de
 * explosão da Bomba Atômica, e a caixa de 4 800 x 3 000 era mais estreita do
 * que essa explosão em boa parte da sua largura - uma carta cujo efeito não
 * cabe no mapa não é uma carta forte, é uma carta que apanha tudo sempre.
 *
 * O número anterior vinha da câmara: com o zoom mínimo de 0,4 um viewport de
 * 1920 mostrava exactamente 4 800 unidades, e a arena era um ecrã de largura.
 * **Isso deixou de valer, e é deliberado.** A câmara segue o casco e já não
 * enquadra a caixa inteira; o que a arena passa a ser é um espaço para
 * atravessar, e não um tabuleiro que se vê todo de uma vez.
 */
export const PVP_ARENA_WIDTH_MU = 24_000_000;
export const PVP_ARENA_HEIGHT_MU = 15_000_000;

/**
 * **A caixa antiga, e onde ela ficou dentro da nova.**
 *
 * O aglomerado de jogo - as saídas, os pads das runas e os impulsos - não foi
 * espalhado pelos 25x. Ele foi **transladado para o centro** com a geometria
 * que tinha, e a razão é de equilíbrio: espalhar multiplicava por cinco todas
 * as distâncias de um duelo, e a 340 unidades por segundo isso transformava a
 * abertura de um combate numa viagem de um minuto. Nada na velocidade, no
 * alcance ou na cadência mudou neste gate, portanto nada que dependa delas
 * podia mudar.
 *
 * O que os 25x dão é **espaço em redor**: sítio para o Leviatã voar, para o
 * Catolé rebentar, e para uma nave fugir de qualquer dos dois. O combate abre
 * exactamente onde abria, à mesma distância e no mesmo ritmo.
 *
 * A linha de simetria continua a ser o eixo vertical da arena - a translação é
 * a mesma nos dois lados, portanto o espelho que `simulation.test.ts` exige
 * sobrevive por construção.
 *
 * **Provisório**, como todo o equilíbrio deste ficheiro.
 */
export const PVP_LEGACY_ARENA_WIDTH_MU = 4_800_000;
export const PVP_LEGACY_ARENA_HEIGHT_MU = 3_000_000;
export const PVP_ARENA_ORIGIN_X_MU = (PVP_ARENA_WIDTH_MU - PVP_LEGACY_ARENA_WIDTH_MU) / 2;
export const PVP_ARENA_ORIGIN_Y_MU = (PVP_ARENA_HEIGHT_MU - PVP_LEGACY_ARENA_HEIGHT_MU) / 2;

export interface PvpObstacle {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * **Vazio, e é uma decisão** (gate V28H.K).
 *
 * ## O que estava aqui, e porque saiu
 *
 * Cinco caixas - um pilar na linha central e um bloco em cada quadrante -
 * espelhadas no eixo vertical. Elas davam cobertura a uma arena que cabia num
 * ecrã.
 *
 * O contrato do catálogo (gate V28H.B) decidiu, no mesmo parágrafo que mandou a
 * arena crescer, que os obstáculos fixos **saem** e que **as naves tanque
 * passam a exercer o papel de bloqueio físico**. Numa caixa de 25x a área, cinco
 * caixinhas paradas não são cobertura: são cinco sítios onde ninguém passa.
 * A cobertura de que um duelo precisa passa a ser outra nave.
 *
 * ## O que ficou no lugar delas
 *
 * O parâmetro `blockers` de `pvpAdvanceShip` (`movement.ts`): **um casco não
 * atravessa outro casco.**
 * Um casco grande ocupa mais espaço - o raio sai da vida máxima, pela mesma
 * curva que o desenha - portanto uma nave tanque É a parede, e uma nave pequena
 * não a empurra. É literalmente o que o contrato pediu, e tem a propriedade que
 * uma caixa parada nunca teve: o bloqueio move-se, decide, e pertence a alguém.
 *
 * ## Porque a lista fica, vazia, em vez de desaparecer
 *
 * Porque o tipo e o percurso continuam a existir - as paredes da arena usam a
 * mesma expulsão - e porque uma lista vazia é uma DECISÃO legível, enquanto uma
 * constante apagada é uma coisa que alguém reintroduz sem saber que foi
 * removida de propósito. `arena-geometry.test.ts` prova que ela está vazia.
 */
export const PVP_OBSTACLES: readonly PvpObstacle[] = [];

/**
 * Where each seat starts, and which way it is already pointing.
 *
 * Transladadas para o centro da arena nova com a separação que sempre tiveram
 * (4 200 000 mu entre os dois cascos): o combate abre à mesma distância, e a
 * simetria é a do eixo vertical da arena, que a translação preserva por
 * construção.
 */
export const PVP_SPAWNS = {
  a: {
    x: PVP_ARENA_ORIGIN_X_MU + 300_000,
    y: PVP_ARENA_HEIGHT_MU / 2,
    aimX: VECTOR_SCALE,
    aimY: 0,
  },
  b: {
    x: PVP_ARENA_ORIGIN_X_MU + 4_500_000,
    y: PVP_ARENA_HEIGHT_MU / 2,
    aimX: -VECTOR_SCALE,
    aimY: 0,
  },
} as const;

/**
 * How far apart a team's ships stand on their own line at the start.
 *
 * Provisional, like every number in this file, and chosen so three hulls at
 * 26 000 mu of radius are clear of each other with room to turn. Along the
 * spawn line rather than across it, so no slot starts closer to the enemy than
 * another - a duel that opened with one ship half an arena ahead would be a
 * balance decision nobody made.
 */
export const PVP_TEAM_SLOT_SPAWN_OFFSET_MU = 400_000;

/**
 * Where one participant starts, from its team and its slot.
 *
 * **Slot 1 is exactly {@link PVP_SPAWNS}**, to the milli-unit. That is not a
 * coincidence to preserve by accident: the shipped 1x1 has only slot 1, so this
 * function returning anything else for it would move both ships of every duel
 * ever played and invalidate every recorded match at once.
 *
 * Slot 2 goes up the line and slot 3 down it, mirrored between the teams by
 * construction because both are measured from their own spawn.
 */
export function pvpSpawnFor(
  team: 'a' | 'b',
  slot: number,
): { readonly x: number; readonly y: number; readonly aimX: number; readonly aimY: number } {
  const base = PVP_SPAWNS[team];
  const offset =
    slot === 1 ? 0 : slot === 2 ? -PVP_TEAM_SLOT_SPAWN_OFFSET_MU : PVP_TEAM_SLOT_SPAWN_OFFSET_MU;
  return { x: base.x, y: base.y + offset, aimX: base.aimX, aimY: base.aimY };
}

/* -------------------------------------------------------------------------- */
/*  Runes                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The four things a duel can pick up.
 *
 * Provisional, like every other number in this file. What is NOT provisional is
 * that all four are decided by the room: a client that could tell the server it
 * had picked something up would be choosing its own damage.
 */
export const PVP_RUNE_KINDS = PVP_RUNE_WIRE_KINDS;

export type PvpRuneKind = PvpRuneWireKind;

/** How long each timed rune lasts, in ticks. Teleport is a charge, not a timer. */
export const PVP_RUNE_DURATION_TICKS: Readonly<Record<PvpRuneKind, number>> = Object.freeze({
  // Nine seconds at 20 Hz.
  double_damage: 180,
  speed: 180,
  /*
   * Zero, and that is the point: a teleport is a charge a player spends when
   * they choose to, not a window they have to use before it closes. Modelled
   * here rather than special-cased at every call site.
   */
  teleport: 0,
});

/** Damage multiplier while Double Damage is held. Integer basis points. */
export const PVP_RUNE_DAMAGE_BPS = 20_000;

/**
 * An unmodified hull's speed. 10 000 bps = 1x, the multiplier that changes
 * nothing.
 *
 * It existed as a bare `10_000` in the room and as a default parameter in
 * `pvpAdvanceShip`, which is part of how the client came to replay movement at
 * a speed the room was not using: there was nothing to name, so nothing to
 * pass.
 */
export const PVP_BASE_SPEED_BPS = 10_000;

/** Speed multiplier while Speed is held. 15 000 bps = 1.5x. */
export const PVP_RUNE_SPEED_BPS = 15_000;

/*
 * `PVP_RUNE_MIRAGE_COUNT` and `PVP_RUNE_MIRAGE_SPREAD_MU` were here.
 *
 * Both left with the mechanic. Nothing replaced them, and the three surviving
 * runes were NOT re-tuned to compensate for the absence - a removal that also
 * rebalanced would be two changes wearing one name.
 */

/**
 * The most teleport charges a ship may hold at once.
 *
 * One. Stacking them would let a player bank an escape for every corner they
 * ever get pushed into, and a duel decided by hoarded charges is not a duel.
 * A second pickup while holding one is refused, and the rune stays on its pad.
 */
export const PVP_RUNE_MAX_TELEPORT_CHARGES = 1;

/*
 * There is deliberately no teleport DISTANCE any more.
 *
 * A fixed 600 000 mu along the aim was a dash, not a teleport: the player
 * pressed a key and the ship slid forward. The design asks them to choose an
 * area, and a chosen point has no length to cap - what bounds it is the arena
 * and its structures, which `pvpValidateTeleport` checks. The constant had no
 * other use, so it is gone rather than left for somebody to reintroduce a
 * range with.
 */

/**
 * Where runes appear, and nowhere else.
 *
 * Fixed pads rather than free positions: a rune that could appear anywhere
 * would eventually appear inside a wall or on top of a player, and both are
 * defects a random position finds eventually rather than never. Mirrored across
 * the arena's centre line for the same reason the obstacles are - a pad one
 * seat reaches first every time is an advantage the server handed out.
 *
 * The middle pad sits on the axis and is its own mirror.
 */
export const PVP_RUNE_PADS: readonly { readonly x: number; readonly y: number }[] = [
  { x: PVP_ARENA_ORIGIN_X_MU + 2_400_000, y: PVP_ARENA_ORIGIN_Y_MU + 600_000 },
  { x: PVP_ARENA_ORIGIN_X_MU + 1_200_000, y: PVP_ARENA_ORIGIN_Y_MU + 1_500_000 },
  { x: PVP_ARENA_ORIGIN_X_MU + 3_600_000, y: PVP_ARENA_ORIGIN_Y_MU + 1_500_000 },
  { x: PVP_ARENA_ORIGIN_X_MU + 2_400_000, y: PVP_ARENA_ORIGIN_Y_MU + 2_500_000 },
];

/** How close a hull's centre must come to a pad to take what is on it. */
export const PVP_RUNE_PICKUP_RADIUS_MU = 60_000;

/** Ticks between a pad being emptied and the next rune appearing on it. */
export const PVP_RUNE_RESPAWN_TICKS = 200;

/** Ticks before the FIRST rune of a match appears, so nobody spawns onto one. */
export const PVP_RUNE_FIRST_SPAWN_TICKS = 60;

/* -------------------------------------------------------------------------- */
/*  Boosters                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Four arrows that shove a ship along, and give the arena a direction.
 *
 * Not obstacles: nothing collides with them and a shot passes straight over.
 * They add a fixed displacement per tick while a hull is inside them, which is
 * why they need no state at all - a booster is a function of where a ship IS,
 * so a room restored from a snapshot resumes shoving without remembering
 * anything.
 *
 * Mirrored in pairs across the centre line, pushing towards the middle, so the
 * map's flow is the same seen from either seat.
 */
export interface PvpBooster {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** Displacement per tick, in milli-units, already a vector. */
  readonly pushX: number;
  readonly pushY: number;
}

/** Half a ship's own speed. Felt, and never enough to fly the ship for you. */
export const PVP_BOOSTER_PUSH_MU_PER_TICK = 8_500;

export const PVP_BOOSTERS: readonly PvpBooster[] = [
  // The outer lanes, pushing inwards along x.
  {
    x: PVP_ARENA_ORIGIN_X_MU + 700_000,
    y: PVP_ARENA_ORIGIN_Y_MU + 1_350_000,
    w: 400_000,
    h: 300_000,
    pushX: PVP_BOOSTER_PUSH_MU_PER_TICK,
    pushY: 0,
  },
  {
    x: PVP_ARENA_ORIGIN_X_MU + 3_700_000,
    y: PVP_ARENA_ORIGIN_Y_MU + 1_350_000,
    w: 400_000,
    h: 300_000,
    pushX: -PVP_BOOSTER_PUSH_MU_PER_TICK,
    pushY: 0,
  },
  // And two that move a ship across the map's short axis.
  {
    x: PVP_ARENA_ORIGIN_X_MU + 2_200_000,
    y: PVP_ARENA_ORIGIN_Y_MU + 900_000,
    w: 400_000,
    h: 300_000,
    pushX: 0,
    pushY: PVP_BOOSTER_PUSH_MU_PER_TICK,
  },
  {
    /*
     * Below where the pillar used to stand.
     *
     * At y 2 100 000 this box overlapped that pillar, which would have shoved a
     * ship into a wall it was already being pushed out of - two rules fighting
     * over the same hull every tick. `runes.test.ts` asserts no booster meets an
     * obstacle, which is how it was found before anybody flew through one. O
     * pilar saiu no gate V28H.K e a prova continua a valer sobre uma lista
     * vazia; a caixa fica onde está, porque mexer nela agora seria mexer no
     * fluxo do mapa por um motivo que já não existe.
     */
    x: PVP_ARENA_ORIGIN_X_MU + 2_200_000,
    y: PVP_ARENA_ORIGIN_Y_MU + 2_500_000,
    w: 400_000,
    h: 300_000,
    pushX: 0,
    pushY: -PVP_BOOSTER_PUSH_MU_PER_TICK,
  },
];

/* -------------------------------------------------------------------------- */
/*  The ship                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * O raio do casco de REFERÊNCIA, em `mu`: o de uma nave com exatamente
 * {@link PVP_MAX_HEALTH} de vida máxima.
 *
 * O raio REAL de um casco é este vezes a sua escala - `pvpHullRadiusMu` - e é
 * ele que colide, que trava nas paredes, que decide onde um tiro nasce e onde
 * uma serra orbita (gate V28G.R). Ninguém deve comparar uma posição contra esta
 * constante: um casco tem o seu.
 */
export const PVP_SHIP_RADIUS_MU = 26_000;

/**
 * **A escala até à qual a arena está PROVADA**, e já não um tecto.
 *
 * ## O que ela era, e o que ela é (gate V28G.R)
 *
 * Era o limite superior de `pvpVisualScale`: um casco nunca era desenhado acima
 * de três vezes a sua arte. Esse `clamp` foi REMOVIDO por decisão do dono - ele
 * tornaria o Coração do Titã invisível para qualquer casco que já lá estivesse,
 * e um tecto sobre o que um item concede é o que o `CLAUDE.md` proíbe. A escala
 * é agora monotónica e sem limite superior.
 *
 * O número fica, com outro papel: é a medida contra a qual
 * `arena-geometry.test.ts` prova que TODOS os corredores e todas as folgas de
 * parede admitem um casco. Enquanto nenhuma composição legal passar daqui, a
 * arena de hoje serve.
 *
 * Com a curva LINEAR do V28G.R esse limite chega aos **3 000** de vida máxima,
 * e o Coração do Titã sozinho chega a 1 600 - `1,60x`. Sobra quase o dobro da
 * folga, e é essa folga que permite a este gate não mexer na arena.
 *
 * **Dependência registada:** a arena tem de crescer substancialmente e perder os
 * seus obstáculos antes de uma composição passar deste número. Nada neste gate a
 * altera.
 *
 * Já não é "presentation only": desde o V28G.R o raio que colide sai da MESMA
 * escala que desenha - ver `hull/scale.ts`.
 */
export const PVP_MAX_VISUAL_SCALE = 3;

/**
 * A escala da nave de REFERÊNCIA, e o recuo de quem ainda não tem perfil.
 *
 * Já não é um mínimo (gate V28G.S). Era-o enquanto a vida-base do duelo era
 * {@link PVP_MAX_HEALTH} e nada descia daí; agora o casco traz a vida máxima
 * autoritativa da conta, e uma conta nova tem 100 - `0,1x`. Pôr um piso aqui
 * faria a curva mentir para todo o domínio abaixo da referência, e apagaria
 * cada ponto de vida comprado lá dentro.
 *
 * O que ele continua a ser é o valor que `pvpVisualScale` devolve para uma
 * entrada que NÃO é uma vida - ausente, zero ou não finita - e a escala que a
 * cena usa para uma cadeira cujo perfil ainda não chegou. Isso é "não sei", e
 * não "é pequeno".
 */
export const PVP_MIN_VISUAL_SCALE = 1;

/** 340 arena units per second at 20 Hz. */
export const PVP_SHIP_SPEED_MU_PER_TICK = 17_000;

/**
 * **A nave-BASE, e ela é a mesma dos dois modos** (gate V28G.V).
 *
 * Foi 1 000 enquanto o duelo normalizava o equipamento fora e todos os cascos
 * eram iguais. Deixou de o ser no gate V28G.S, quando `profile.maxHealthMilli`
 * passou a ser a vida máxima AUTORITATIVA da conta - a mesma que a caça usa -
 * mas o 1 000 ficou aqui como denominador da geometria, e ninguém os comparou.
 *
 * A consequência apareceu na primeira sala aberta com contas REAIS: uma conta
 * nova tem 100 de vida, `100 / 1 000` dava **0,1x**, e o casco era desenhado com
 * quatro píxeis. Nenhuma prova o apanhou porque todas usam o perfil por omissão,
 * e o perfil por omissão trazia exatamente 1 000 - o único casco que media `1x`
 * era o que nenhum jogador tem.
 *
 * Agora é DERIVADO de {@link SHIP_BASE_MAX_HEALTH}, que é neutro e vive em
 * `hull/`. Não há um segundo 100 escrito dentro do PvP, e `pvp/` não importa
 * `hunt/` para o saber.
 *
 * ## Os dois papéis, e porque um número serve os dois
 *
 * É o denominador da métrica do casco - `escala = vidaMaxima / base` - e é a
 * vida máxima do PERFIL POR OMISSÃO, que uma cadeira vazia carrega antes de
 * alguém se sentar nela. Os dois querem dizer a mesma coisa: uma nave sem um
 * ponto alocado, sem artefacto, sem equipamento e sem joia.
 *
 * | vida máxima | escala |
 * | ---: | ---: |
 * | 100 | 1x |
 * | 101 | 1,01x |
 * | 160 | 1,6x |
 * | 1 000 | 10x |
 * | 10 000 | 100x |
 *
 * Linear, sem teto e sem piso: cada ponto de vida acrescenta exatamente 0,01x.
 */
export const PVP_MAX_HEALTH = SHIP_BASE_MAX_HEALTH;

/**
 * A mesma nave-base, na unidade em que a vida vive (gate V28G.S.1).
 *
 * `100 000` milli. É este o número que a métrica do casco divide, e é este o
 * teto que uma cadeira ainda vazia carrega.
 *
 * A unidade CANCELA na curva - `vidaMilli / referenciaMilli` é a mesma razão que
 * `vida / referencia` - portanto a tabela acima vale nas duas unidades.
 * `hull/scale.test.ts` prova esse cancelamento diretamente.
 *
 * Derivado e não escrito, para que não haja um dia em que os dois discordem.
 */
export const PVP_MAX_HEALTH_MILLI = SHIP_BASE_MAX_HEALTH_MILLI;

/* -------------------------------------------------------------------------- */
/*  The gun                                                                   */
/* -------------------------------------------------------------------------- */

export const PVP_PROJECTILE_RADIUS_MU = 7_000;

/** 1 240 arena units per second. Fast enough to need leading, slow enough to dodge. */
export const PVP_PROJECTILE_SPEED_MU_PER_TICK = 62_000;

/** Five volleys a second. */
export const PVP_FIRE_INTERVAL_TICKS = 4;

export const PVP_PROJECTILE_DAMAGE = 40;

/** Three seconds. A shot that hits nothing expires; it never re-acquires. */
export const PVP_PROJECTILE_LIFETIME_TICKS = 60;

/**
 * How far a projectile is stepped WITHIN one tick before it is tested for hits.
 *
 * A shot covers 62 000 mu per tick and a hull is 52 000 mu across, so a single
 * end-of-tick test would let a shot pass clean through a ship - the oldest bug
 * in projectile collision. Four substeps put every shot at 15 500 mu per test,
 * comfortably inside both a hull and the thinnest wall.
 *
 * Substepping the shot rather than sweeping it analytically keeps the maths
 * integer: a swept circle-circle test needs a discriminant that overflows
 * `Number.MAX_SAFE_INTEGER` at arena scale, and reaching for `bigint` twenty
 * times a second to avoid four cheap comparisons is the wrong trade.
 */
export const PVP_PROJECTILE_SUBSTEPS = 4;

/**
 * The most projectiles one match may have in flight.
 *
 * A ceiling, not a design value: at five shots a second each living three
 * seconds, two ships hold about 30. It exists so a bug in the cadence cannot
 * turn one room into an unbounded allocation, and it is high enough that
 * reaching it is a defect rather than gameplay.
 *
 * This is NOT the hunt's cadence problem. `CLAUDE.md` forbids capping the
 * attack speed an ITEM grants, and no item grants anything here - both ships
 * fire at a fixed, normalised rate that no player input can change.
 */
export const PVP_MAX_PROJECTILES = 256;

/* -------------------------------------------------------------------------- */
/*  O Núcleo Atômico (gate V28G.J)                                            */
/* -------------------------------------------------------------------------- */

/**
 * A velocidade da bomba, em basis points da do projétil que ela substituiu.
 *
 * **80 %**, decidido pelo dono. Em basis points e não como uma segunda
 * constante de velocidade, pela mesma razão que a runa de Velocidade é um
 * multiplicador: um número escrito à mão aqui deixaria de ser 80 % da velocidade
 * real no dia em que {@link PVP_PROJECTILE_SPEED_MU_PER_TICK} mudasse, e
 * ninguém iria à procura dele.
 */
export const PVP_BOMB_SPEED_BPS = 8_000;

/**
 * O quanto a silhueta da bomba cresceu, medida contra um projétil comum: **+190 %**.
 *
 * Duas revisões visuais, e a segunda avaliou a primeira: 145 pôs a bomba a
 * ocupar 40,6 unidades de mundo e a leitura foi que ainda era pequena. **290** é
 * o dobro disso, e é o número que o dono aprovou depois de a ver a voar.
 *
 * O que ele NÃO faz é mexer na forma. A proporção - o cilindro grosso, o nariz
 * curto, as aletas - vive na textura, e ela não mudou: a bomba é a mesma bomba,
 * desenhada ao dobro. Crescer redesenhando seria arriscar a silhueta aprovada
 * para obter uma coisa que uma multiplicação já dá.
 *
 * Em basis points inteiros, para a conta abaixo ser exata: `14 000 x 290 / 100`
 * são 40 600 sem resto.
 */
export const PVP_BOMB_BODY_GROWTH_BPS = 290;

/**
 * O CORPO da bomba, em unidades de mundo.
 *
 * O dobro de um projétil comum, e depois quase o triplo disso: o contrato pede
 * uma silhueta que ninguém confunda com uma bala, e nem o dobro nem os 45 % da
 * primeira revisão chegaram. São 40 600 mu - um casco e meio de comprimento. É a
 * medida que a arte usa para se dimensionar, e **não** é uma caixa de colisão: a
 * bomba não colide com um casco pelo corpo, colide pela proximidade. O que ela
 * decide, mecanicamente, é a espessura com que a bomba é testada contra paredes
 * e obstáculos - um corpo maior toca a pilastra mais cedo, que é o que uma bomba
 * maior faz.
 */
export const PVP_BOMB_BODY_RADIUS_MU =
  (PVP_PROJECTILE_RADIUS_MU * 2 * PVP_BOMB_BODY_GROWTH_BPS) / 100;

/**
 * Quanto a explosão cresceu, medida contra os três cascos originais: **+350 %**.
 *
 * Três avaliações manuais, cada uma a olhar para o resultado da anterior:
 * `100 -> 120` (+20 %), `120 -> 300` (2,5x) e agora `300 -> 450` (+50 %).
 * Treze cascos e meio de raio, ou 351 000 mu.
 *
 * **E isto é dano, não decoração.** A onda desenhada é o raio mecânico - é o que
 * {@link PVP_BOMB_BLAST_RADIUS_MU} diz e o que `ATOMIC_WAVE_TRUTH` prova - por
 * isso uma explosão duas vezes e meia maior atinge duas vezes e meia mais longe.
 * Não há aqui um número de arte separado que se pudesse ter subido sozinho: um
 * círculo desenhado maior do que o círculo que fere seria o desenho a mentir
 * sobre quem levou.
 */
export const PVP_BOMB_BLAST_GROWTH_BPS = 450;

/**
 * **O raio da explosão: treze cascos e meio.**
 *
 * O contrato visual do §5 era "diâmetro equivalente a aproximadamente três naves
 * PvP de tamanho normal" - `3 x PVP_SHIP_RADIUS_MU` de raio, 78 000 mu. Três
 * avaliações manuais depois, {@link PVP_BOMB_BLAST_GROWTH_BPS} multiplica-o por
 * quatro e meio: **351 000 mu**, ou treze cascos e meio de raio.
 *
 * Para escala, e porque a esta altura convém dizê-lo: é mais de um catorze avos
 * da largura da arena. Uma bomba que rebente entre os dois cascos apanha os
 * dois, e é isso que uma bomba atómica deve fazer.
 *
 * DERIVADO do casco, e não escrito. É este mesmo valor que o VFX desenha como
 * onda de choque: o círculo que o jogador vê é o raio que a simulação usou, e não
 * uma aproximação artística dele.
 *
 * **Não cresce com o nível nem com o número de cópias.** O que a joia escala é a
 * magnitude, e mais nada.
 */
export const PVP_BOMB_BLAST_RADIUS_MU = (PVP_SHIP_RADIUS_MU * 3 * PVP_BOMB_BLAST_GROWTH_BPS) / 100;

/**
 * O raio de PROXIMIDADE: o corpo da bomba, mais 40 % de folga.
 *
 * A ordem que o contrato exige, e que se lê aqui sem contas:
 *
 *     corpo (40 600) < proximidade (56 840) < explosão (351 000)
 *
 * **Era metade da explosão, e deixou de ser.** Quando a explosão foi a 2,5x, essa
 * derivação levava a caixa de disparo a 117 000 - uma bomba a rebentar a quatro
 * cascos e meio de distância do alvo, que não é a distância de detonação que
 * ninguém aprovou. O tamanho da explosão e a distância a que ela dispara são
 * decisões diferentes, e passam a estar escritas como duas.
 *
 * Presa ao CORPO, porque é o corpo que ela tem de conter. O contrato visual pede
 * que a caixa de proximidade cubra a silhueta inteira - aletas incluídas - e o
 * círculo que envolve a silhueta é `1,13 x` o raio do corpo. Os 40 % dão-lhe
 * essa margem por construção, em vez de a deixarem depender de um raio de
 * explosão que já mudou duas vezes.
 *
 * Um inimigo que entra nele faz a bomba detonar; o alvo direto é o mais PRÓXIMO,
 * e um empate resolve-se pelo id canónico. A contenção da silhueta fica provada
 * em `atomic-bomb-art.test.ts` contra as dimensões reais da textura, e não
 * contra um número copiado para aqui.
 */
export const PVP_BOMB_PROXIMITY_RADIUS_MU = (PVP_BOMB_BODY_RADIUS_MU * 14) / 10;

/**
 * **E o da Esfera Ígnea: metade outra vez** (gate V28H.W).
 *
 * "O raio de colisão deve ser 50% maior", decisão do dono depois de a ver
 * atravessar um casco antes de rebentar. Ela é a única que muda: a Bomba
 * Atômica de uma joia continua com o dela, e o Catolé também - a proximidade
 * dele é o que o faz rebentar em vez de orbitar, e mexer-lhe seria mexer numa
 * carta aprovada.
 *
 * Escrito como uma multiplicação sobre o da Bomba, e não como um número: a
 * palavra "50% maior" continua verdadeira se a Bomba mudar.
 */
export const PVP_FIREBALL_PROXIMITY_RADIUS_MU = (PVP_BOMB_PROXIMITY_RADIUS_MU * 150) / 100;

/**
 * O deslocamento MÁXIMO de referência do empurrão: 360 000 unidades de mundo.
 *
 * Eram 250 000 - o número que os 25 % não-dano do orçamento da joia compram (ver
 * `docs/gates/v28gf-proc-math.mjs`, `nonDamage`) - depois 300 000, e a segunda
 * avaliação manual pediu mais 20 %. É a distância que um casco no CENTRO exato da
 * explosão percorre; à borda percorre um quarto dela, porque a queda linear não
 * mudou.
 *
 * Para escala: quase sete cascos, e mais de metade da largura de um dos
 * obstáculos mais estreitos da arena.
 *
 * A DURAÇÃO não acompanha - continuam a ser os mesmos sete tiques - portanto o
 * casco percorre mais chão no mesmo tempo, que é o que "empurrão mais forte"
 * quer dizer.
 */
export const PVP_BOMB_KNOCKBACK_MU = 360_000;

/** A duração do empurrão, em milissegundos. Decisão do dono. */
export const PVP_BOMB_KNOCKBACK_MS = 350;

/**
 * A duração do empurrão em TIQUES, derivada e exata.
 *
 * `350 / 50 = 7`, sem resto - e é por isso que a divisão está aqui em vez de
 * uma constante `7` escrita à mão: se algum dia a duração ou o tique deixarem de
 * dividir, isto passa a ser um número fracionário e o erro aparece na compilação
 * do teste em vez de aparecer como um empurrão que dura 6,8 tiques.
 */
export const PVP_BOMB_KNOCKBACK_TICKS = PVP_BOMB_KNOCKBACK_MS / PVP_TICK_MS;

/**
 * A força na BORDA da explosão, em basis points da força no centro: **25 %**.
 *
 * A queda é LINEAR entre os dois - `10 000` no centro, `2 500` à borda - e não
 * quadrática nem por degraus: o contrato diz "queda linear até 25 % da força na
 * borda", e uma curva inventada aqui seria equilíbrio decidido num ficheiro de
 * constantes.
 */
export const PVP_BOMB_KNOCKBACK_EDGE_BPS = 2_500;

/* -------------------------------------------------------------------------- */
/*  O Arsenal Ígneo (gate V28G.L)                                             */
/* -------------------------------------------------------------------------- */

/**
 * A velocidade do míssil: **170 %** da de um tiro comum, derivada.
 *
 * `62 000 x 6 060 / 10 000 = 37 572` mu por tique, sem resto - e é o "sem
 * resto" que escolhe o valor de {@link MISSILE_SPEED_BPS}, não o contrário: uma
 * fração aqui seria truncada a cada tique, e o míssil andaria menos do que diz.
 * Derivada e não escrita, pela mesma razão que a da Bomba é: um número absoluto
 * deixaria de significar um múltiplo do tiro no dia em que o tiro mudasse de
 * velocidade.
 *
 * A esta velocidade o míssil percorre 9 393 mu por sub-passo dos quatro que um
 * projétil já usa - bem abaixo da caixa de teste mais fina que ele tem de
 * respeitar (`casco + hitbox` = 61 525 mu) e da parede mais estreita da arena
 * (200 000 mu, mais o corpo dos dois lados). Não precisa de sub-passos próprios,
 * e dar-lhos seria uma segunda constante a divergir da primeira.
 */
export const PVP_MISSILE_SPEED_MU_PER_TICK =
  (PVP_PROJECTILE_SPEED_MU_PER_TICK * MISSILE_SPEED_BPS) / 10_000;

/**
 * **Nove segundos, em tiques.** `9 000 / 50 = 180`, exato e sem resto.
 *
 * A divisão está aqui em vez de um `180` escrito à mão exatamente como a do
 * empurrão está: se algum dia a duração ou o tique deixarem de dividir, isto
 * passa a ser fracionário e o erro aparece numa prova em vez de aparecer como um
 * míssil que vive 8,96 segundos.
 */
export const PVP_MISSILE_LIFETIME_TICKS = MISSILE_LIFETIME_MS / PVP_TICK_MS;

/**
 * O CORPO do míssil: **70 %** do corpo da Bomba, em unidades de mundo.
 *
 * `40 600 x 0,7 = 28 420` mu, sem resto. É a medida de que a arte se serve para
 * se dimensionar - o comprimento desenhado é o dobro disto - e é o que faz o
 * §"Tamanho" do contrato ser verdade por construção: 70 % do comprimento visual
 * e 70 % da largura máxima saem os dois de uma escala UNIFORME sobre a mesma
 * silhueta, portanto a proporção não muda com o zoom.
 *
 * Derivada da Bomba e nunca copiada dela: "70 % da Bomba" continua a ser verdade
 * quando a Bomba mudar de tamanho, que já aconteceu duas vezes.
 *
 * Mecanicamente decide a espessura com que o míssil é testado contra paredes e
 * limites - um corpo maior toca a pilastra mais cedo.
 */
export const PVP_MISSILE_BODY_RADIUS_MU = (PVP_BOMB_BODY_RADIUS_MU * MISSILE_SIZE_BPS) / 10_000;

/**
 * A HITBOX do míssil contra um casco: o corpo mais a margem declarada.
 *
 * `28 420 x 1,25 = 35 525` mu. Contém a silhueta desenhada inteira - aletas
 * incluídas, cujo círculo envolvente é `1,1334 x` o meio-comprimento - com cerca
 * de 10 % de folga, e é essa a "margem pequena e declarada" do contrato.
 *
 * **Não é o raio de proximidade da Bomba**, e a diferença é a diferença entre as
 * duas entidades: a Bomba detona ao APROXIMAR-SE (`1,4 x` o corpo, e o corpo
 * dela é maior), o míssil exige colisão física. O de 56 840 mu da Bomba é
 * exatamente o dobro deste, e usá-lo faria o míssil rebentar ao largo.
 */
export const PVP_MISSILE_HIT_RADIUS_MU = (PVP_MISSILE_BODY_RADIUS_MU * MISSILE_HITBOX_BPS) / 10_000;

/**
 * **A rotação de um tique, ESCRITA e não calculada.**
 *
 * 240 graus por segundo x 50 ms = **12 graus por tique**, e o par abaixo é
 * `round(cos 12° x 65 536)` e `round(sin 12° x 65 536)`.
 *
 * ## Porque escrita
 *
 * Pela mesma razão que {@link VECTOR_DIAGONAL} é escrita: `Math.cos` é
 * implementation-defined no último bit, e Node e Chromium já discordam noutro
 * sítio deste repositório. Um duelo decidido por um míssil que virou um
 * milionésimo de grau a mais num dos dois lados é um duelo decidido pelo motor
 * de JavaScript.
 *
 * Uma prova refaz a conta com `Math.round(Math.cos(...) * VECTOR_SCALE)` e
 * compara - o arredondamento mata a diferença do último bit, portanto a prova é
 * estável e o SIMULADOR nunca chama trigonometria nenhuma.
 *
 * ## O que a rotação faz, e o que a impede de oscilar
 *
 * O par roda o vetor de direção em `±12°`, e o sentido sai do produto externo
 * contra a direção desejada. Quando o ângulo que falta é MENOR do que 12° a
 * rotação passaria do alvo, e isso deteta-se pela troca de sinal do produto
 * externo - exata, inteira e sem tolerância - e o míssil assenta exatamente na
 * direção desejada em vez de ficar a oscilar meio grau para cada lado.
 */
export const PVP_MISSILE_TURN_COS = 64_104;
export const PVP_MISSILE_TURN_SIN = 13_626;

/* -------------------------------------------------------------------------- */
/*  Connection                                                                */
/* -------------------------------------------------------------------------- */

/**
 * How often a running room writes a compact snapshot to Durable Object storage.
 *
 * NOT every tick. At 20 Hz that would be twenty writes a second per match to
 * record a world that changes by a few thousand milli-units - a storage bill
 * and a latency cost paid to protect against an eviction that happens far less
 * often than once a second. Once a second bounds what an eviction can lose to
 * one second of movement, which the players will read as a small correction.
 *
 * Critical TRANSITIONS do not wait for this. A match becoming ready, running,
 * disconnected, reconnected or finished is written before it is announced, so
 * there is no window in which a player has been told something the room would
 * not remember.
 */
export const PVP_PERSIST_INTERVAL_MS = 1_000;

/**
 * How often the watchdog alarm looks at a room nobody is talking to.
 *
 * This is NOT the game loop - see the note on `PvpRoom.pump`. The simulation is
 * driven by incoming messages, and this exists for the case where there are
 * none: both players dropped, or both stood still while the clock ran out.
 * Without it, an abandonment would be noticed only when somebody came back.
 *
 * One second is far coarser than a tick and far finer than the twelve-second
 * grace window, which is the only deadline it has to catch.
 */
export const PVP_WATCHDOG_INTERVAL_MS = 1_000;

/**
 * How long a dropped seat has to come back before it forfeits.
 *
 * Long enough for a Wi-Fi handover or a browser tab restore, short enough that
 * the player still connected is not held hostage by somebody who closed their
 * laptop. Configurable, because the right value is an observation nobody has
 * made yet.
 */
export const PVP_RECONNECT_GRACE_MS = 12_000;

/**
 * How long an unaccepted challenge stays open.
 *
 * The code is only useful while somebody is waiting to hear it.
 */
export const PVP_CHALLENGE_TTL_MS = 10 * 60 * 1_000;

/**
 * How long a room stays alive after the duel ends.
 *
 * Enough for both clients to read the result and for a reconnecting client to
 * be told the match is over rather than finding nothing. After it, the room
 * stops its timer and lets itself go - a room that keeps ticking with nobody in
 * it burns CPU for as long as the runtime keeps it, which the workerd probe
 * demonstrated by accident before this constant existed.
 */
export const PVP_ROOM_LINGER_MS = 30_000;

/* -------------------------------------------------------------------------- */
/*  How long a running match may POSSIBLY last, in wall clock                 */
/* -------------------------------------------------------------------------- */

/**
 * The slowest the simulation may legitimately advance against the wall clock.
 *
 * Derived, not chosen. The room advances at most {@link PVP_MAX_CATCHUP_MS} of
 * simulated time per pump, and in the worst legitimate case the only thing
 * pumping it is the watchdog at {@link PVP_WATCHDOG_INTERVAL_MS}. A room in
 * that state - sockets open, so nobody is abandoning, but no message arriving
 * to drive it, which is what a throttled background tab looks like - runs at
 * exactly this fraction of real time.
 *
 * Four, today, from 1000 / 250. Written as the division so that changing either
 * constant moves this with it rather than leaving a stale number behind.
 */
export const PVP_WORST_CASE_SLOWDOWN = PVP_WATCHDOG_INTERVAL_MS / PVP_MAX_CATCHUP_MS;

/**
 * The longest a `running` match can still be honest, measured from its first
 * tick in WALL CLOCK.
 *
 * ## What this is for
 *
 * A match whose room dies is never finished by anybody: the room is the only
 * thing that reports a result, so the row stays `running` and its participants
 * stay active for ever. Recovering that needs an answer to one question the
 * database cannot otherwise ask - "could this match still be being played?" -
 * and the answer has to be derived from the server's own numbers rather than
 * from a timer that died with the room.
 *
 * ## Every term, and why it is here
 *
 * - `PVP_TIME_LIMIT_TICKS * PVP_TICK_MS` - the simulation cannot run longer
 *   than its own limit. Two minutes, and the same for both rulesets.
 * - `* PVP_WORST_CASE_SLOWDOWN` - because that is SIMULATED time, and the wall
 *   clock may legitimately run four times faster than it.
 * - `+ PVP_RECONNECT_GRACE_MS` - a chair that drops on the last tick is still
 *   owed its grace before anything about it is decided.
 * - `+ PVP_ROOM_LINGER_MS` - the room stays up after the result so both clients
 *   can read it, and its audit event is delivered from that window.
 *
 * Eight minutes and forty-two seconds, today. Deliberately generous: ending a
 * match somebody is still playing is the one outcome this must never produce,
 * and the cost of being late is that an account waits a few more minutes for a
 * room that is already gone.
 *
 * It is NOT a game rule and nothing in the simulation reads it. It is the
 * server's floor for calling a match abandoned by its own infrastructure.
 */
export const PVP_MAX_MATCH_WALL_CLOCK_MS =
  PVP_TIME_LIMIT_TICKS * PVP_TICK_MS * PVP_WORST_CASE_SLOWDOWN +
  PVP_RECONNECT_GRACE_MS +
  PVP_ROOM_LINGER_MS;

/* -------------------------------------------------------------------------- */
/*  O Orbe da Víbora (gate V28G.M)                                            */
/* -------------------------------------------------------------------------- */

/**
 * **Um segundo, em tiques.** `1 000 / 50 = 20`, exato e sem resto.
 *
 * A divisão está aqui em vez de um `20` escrito à mão, exatamente como a do
 * tempo de vida do míssil está: se algum dia o intervalo ou o tique deixarem de
 * dividir, isto passa a ser fracionário e o erro aparece numa prova em vez de
 * aparecer como um stack que cobra a terceira parcela a 2,95 segundos.
 *
 * É a ÚNICA constante que o veneno acrescenta ao equilíbrio do duelo. Chance,
 * magnitude, contagem de parcelas e duração vivem em `damage/poison.ts`, que é
 * onde os dois modos as leem - um segundo par de números aqui seria um segundo
 * dono para uma decisão que já tem um.
 */
export const PVP_POISON_TICK_INTERVAL_TICKS = POISON_TICK_INTERVAL_MS / PVP_TICK_MS;

/**
 * **A espessura do feixe da Lança Solar**, em bps do raio do casco (V28H.L).
 *
 * Meia espessura, e é o que se compara com a distância de um casco ao segmento.
 * 35% de um raio dá um feixe com sete décimos de raio de espessura total -
 * grosso o suficiente para se ler no meio de um combate, estreito o suficiente
 * para se sair da linha dele, que é a contrapartida de a nave estar presa a
 * carregar.
 *
 * Provisório, como todo o equilíbrio deste ficheiro. Ele vive aqui e não em
 * `cards/balance.ts` porque é uma medida de ARENA - o mesmo motivo pelo qual a
 * forma dos Super Canhões se dividiu entre os dois sítios.
 */
export const PVP_SOLAR_LANCE_WIDTH_BPS = 22_000;

/**
 * **O alcance do feixe da Lança Solar no duelo**, em bps do raio do casco.
 *
 * ## Porque o ALCANCE se dividiu em dois (gate V28H.P)
 *
 * Ele viveu em `cards/balance.ts`, uma proporção só para os dois modos - e o dono
 * viu o resultado: "a lança solar está muito pequena e de baixo alcance" no
 * duelo, depois de a aprovar na caça.
 *
 * A medição explica-o. A proporção é do CASCO, e os dois cascos não ocupam o
 * mesmo ecrã: a nave da caça mede 96 de 720 - **13 %** da altura da arena - e a
 * do duelo mede 52 000 de 15 000 000, que ao zoom por omissão dá **2 %** da
 * largura do ecrã. O mesmo múltiplo do casco produzia um feixe 3,7 vezes mais
 * curto e mais fino num modo do que no outro.
 *
 * Portanto o alcance passou a ser uma medida de ARENA, como a espessura já era,
 * e pela mesma razão exacta. Os dois números abaixo são escolhidos para o feixe
 * ocupar a MESMA fracção do ecrã nos dois modos - que é o que "a mesma carta"
 * quer dizer para quem olha.
 *
 * 900 000 é 90 raios - 2 340 000 mu num casco de 26 000 - e ao zoom por omissão
 * isso desenha cerca de 92 % da largura do ecrã, que é a mesma fracção que os
 * 1 200 unidades da caça ocupam nos 1 280 dela.
 *
 * Provisório, como todo o equilíbrio deste ficheiro.
 */
export const PVP_SOLAR_LANCE_REACH_BPS = 900_000;

/**
 * Um alcance que cobre a arena inteira, em bps do raio do casco.
 *
 * Para as cartas cuja escolha de alvo NÃO tem limite de distância - o Catolé,
 * que persegue. Derivado da arena e do casco de referência em vez de ser um
 * número grande escolhido à mão: se a arena crescer outra vez, isto cresce com
 * ela, e nunca fica curto por acidente.
 */
export const PVP_CARD_UNLIMITED_REACH_BPS = Math.ceil(
  (PVP_ARENA_WIDTH_MU * 10_000) / PVP_SHIP_RADIUS_MU,
);

/**
 * Quanto um projétil teleguiado corrige o rumo por tique, em unidades de vector.
 *
 * Uma fracção pequena de `VECTOR_SCALE`: ele **curva**, e não vira. Um valor
 * grande faria o Catolé colar-se ao alvo e tornaria a esquiva impossível; um
 * valor pequeno faria a palavra "teleguiada" não querer dizer nada.
 *
 * Provisório, como todo o equilíbrio deste ficheiro.
 */
export const PVP_CARD_HOMING_BPS = Math.trunc(VECTOR_SCALE / 3);

/**
 * A que fracção da velocidade de um tiro voa um projétil de CARTA.
 *
 * Quarenta por cento, e o número não é de gosto: uma perseguição só fecha
 * quando o raio da curva - velocidade a dividir pelo ângulo corrigido por
 * tique - é menor do que o raio a que ela apanha o alvo. À velocidade de um
 * tiro comum o Catolé orbitava o adversário para sempre, medido.
 *
 * Uma bomba colossal e lenta é também o que a descrição dela sugere, e o que
 * torna possível fugir-lhe se se reagir depressa.
 */
export const PVP_CARD_PROJECTILE_SPEED_BPS = 4_000;

/**
 * **E a Esfera Ígnea voa 30% mais depressa do que isso** (gate V28H.W).
 *
 * Decisão do dono, a vê-la atravessar a arena. Ela pode: ao contrário do
 * Catolé, **não persegue ninguém** - sai a direito na direcção apontada - e
 * portanto não tem uma curva de perseguição que a velocidade possa abrir. O
 * argumento geométrico que prende o Catolé aos 40% não se aplica a ela.
 *
 * Uma bola de setenta cascos de raio a arrastar-se lia-se como um balão, e
 * quinze segundos de carga merecem chegar lá.
 *
 * **Duas vezes trinta por cento** (gate V28H.X): o dono viu-a com o primeiro
 * aumento e pediu outro igual. 4 000 -> 5 200 -> 6 760 bps, ou seja 1,69x a
 * velocidade a que o Catolé viaja e dois terços da de um tiro comum. Continua a
 * ser possível vê-la a caminho, que é a razão pela qual ela não voa como uma
 * bala.
 */
export const PVP_FIREBALL_SPEED_BPS = (PVP_CARD_PROJECTILE_SPEED_BPS * 169) / 100;

/**
 * **A que velocidade voam os dois dardos de gelo** (gate V28H.W).
 *
 * O mesmo número que a Esfera Ígnea usa hoje, e escrito por extenso em vez de
 * derivado dela: é o mais depressa que um corpo de carta foi medido a ainda
 * fechar uma perseguição, e é isso que ele diz aqui. Derivá-lo da Esfera faria
 * uma decisão de equilíbrio sobre a bola de fogo mover o gelo sem ninguém a
 * pedir.
 *
 * Os 40% do Catolé não servem: uma bomba colossal arrasta-se por desenho, mas um
 * dardo que demora oito segundos a atravessar a distância de abertura não é um
 * dardo - o alvo já lá não está, e a carta lê-se como avariada.
 *
 * **A velocidade de um TIRO** (segunda medição): com 6 760 bps o dardo demorava
 * cinco segundos a cruzar a distância de abertura, e cinco segundos de viagem é
 * tempo a mais para uma coisa que só põe um temporizador. O argumento geométrico
 * que prende o Catolé aos 40% - a curva de perseguição não fechar - deixou de se
 * aplicar quando o dardo passou a SAIR na direcção do alvo: ele já nasce
 * apontado, e a perseguição só tem de corrigir o que o alvo andar entretanto.
 */
export const PVP_ICE_BOLT_SPEED_BPS = 10_000;

/**
 * **O raio da explosão da Esfera Ígnea no DUELO**: setenta raios de casco.
 *
 * Afinado com o dono a segurar a tecla, ao longo de três avaliações: seis, depois
 * catorze, e agora setenta. Para escala, são **86% do raio do Catolé** (81
 * cascos), que é a maior explosão do jogo - a bola cheia chega perto dela e fica
 * abaixo, que é a linha que mantém o Catolé a ser o que ele é.
 *
 * Aqui e não no catálogo das cartas (gate V28H.Z): a caça tem outra escala, e
 * setenta raios de casco ali seriam duas vezes e meia a arena inteira.
 *
 * E o raio é o que se DESENHA: o corpo é uma fracção dele, e desde este gate é
 * também o que ela APANHA. A bola que se vê crescer é a bola que fere.
 */
export const PVP_FIREBALL_REACH_BPS = 700_000;

/**
 * **Quanto tempo a Esfera Ígnea carrega no DUELO: quinze segundos, à mão.**
 *
 * Aprovado pelo dono depois de três avaliações com a tecla na mão. É o preço da
 * maior bola do jogo, e paga-se com a nave presa e a mira livre - quem não o
 * quiser pagar solta quando quiser, e a bola sai com o que ela vale nesse
 * instante.
 *
 * Aqui e já não no catálogo (gate V28I.C): a caça carrega sozinha em 400 ms, e
 * um número só não pode servir duas cartas que se jogam de maneiras opostas.
 */
export const PVP_FIREBALL_CHARGE_MS = 15_000;

/**
 * **Quantos degraus de um segundo o Congelamento pode durar** (gate V28H.W).
 *
 * Três: dois, três ou quatro segundos, a partir do mínimo que o catálogo guarda.
 * Degraus inteiros e não um contínuo, porque a arte tem TRÊS camadas de gelo
 * desenhadas - leve, média, intensa - e elas só dizem a verdade se a duração
 * cair sempre num dos três valores.
 */
export const PVP_FREEZE_DURATION_STEPS = 3;

/**
 * **E com que probabilidade ele também trava o ataque**: trinta por cento.
 *
 * O número que faltava à descrição aprovada, escrito pelo dono no gate V28H.W.
 * Sorteado pela sala a partir do id do projéctil, portanto igual na primeira
 * passagem e na re-simulação - ver `rolls.ts`.
 *
 * As duas coisas são independentes: uma esfera de quatro segundos pode não
 * travar o ataque, e uma de dois pode travá-lo.
 */
export const PVP_FREEZE_ATTACK_BLOCK_BPS = 3_000;

/**
 * **Quantos degraus a Lentidão pode durar**: quatro - três a seis segundos.
 *
 * A mesma disciplina do Congelamento, e sem camadas a acompanhar: o que a
 * Lentidão desenha são flocos, e flocos não têm de contar segundos.
 */
export const PVP_SLOW_DURATION_STEPS = 4;

/**
 * **O raio de explosão do Catolé: literalmente SEIS VEZES o da Bomba Atômica.**
 *
 * Decisão APROVADA pelo dono e registada no contrato do catálogo (gate V28H.B).
 * Escrita como um múltiplo da constante da joia e nunca como um número solto:
 * se a Bomba mudar, isto muda com ela, e a palavra "6x" continua verdadeira.
 *
 * É também a razão de a arena ter tido de crescer antes desta carta (gate
 * V28H.K): dois milhões de mu de raio não cabiam numa caixa de 4 800 000 de
 * largura sem apanharem o mapa inteiro.
 */
export const PVP_CATOLE_BLAST_RADIUS_MU = PVP_BOMB_BLAST_RADIUS_MU * 6;

/**
 * **O empurrão do Catolé: 3x o da Bomba Atômica** (gate V28H.U).
 *
 * Decisão aprovada, escrita como multiplicação e não como número: a palavra
 * "3x" continua verdadeira se a Bomba mudar, e um valor copiado deixaria de o
 * ser sem ninguém reparar.
 */
export const PVP_CATOLE_KNOCKBACK_MU = PVP_BOMB_KNOCKBACK_MU * 3;

/**
 * **E o CORPO dele: 3x o da Bomba**, pela mesma disciplina.
 *
 * Ele é "uma Bomba Atômica superior", e a primeira coisa que diz isso é o
 * tamanho da coisa que atravessa a arena. Em unidades de milli, como tudo o
 * resto desta arena.
 */
export const PVP_CATOLE_BODY_RADIUS_MU = PVP_BOMB_BODY_RADIUS_MU * 3;

/**
 * Quantos tiques vive um projétil de CARTA.
 *
 * Muito mais do que um tiro comum, e por uma razão de custo: um tiro é barato e
 * há muitos, uma Esfera Ígnea custa vinte e dois segundos e um Catolé custa
 * sessenta. Com o tempo de vida de um tiro, o Catolé expirava a meio da arena
 * nova e a palavra "teleguiada" não queria dizer nada.
 *
 * Derivado da arena e da velocidade em vez de escolhido: é o que ele precisa
 * para atravessar a caixa inteira, com folga para curvar pelo caminho.
 */
export const PVP_CARD_PROJECTILE_LIFETIME_TICKS = Math.ceil(
  (PVP_ARENA_WIDTH_MU / PVP_PROJECTILE_SPEED_MU_PER_TICK) * 1.5,
);

/**
 * O dano do kamikaze de um Drone, em bps do dano-base do dono.
 *
 * Trezentos por cento: é o fim dele, e acontece uma vez. **Provisório.**
 */
export const PVP_DRONE_KAMIKAZE_BPS = 30_000;

/** O raio do kamikaze, em bps do raio do casco do dono. **Provisório.** */
export const PVP_DRONE_KAMIKAZE_REACH_BPS = 40_000;

/**
 * A que distância do centro de uma invocação nasce um tiro dela.
 *
 * O suficiente para o primeiro sub-passo não nascer já dentro do corpo dela,
 * que é a mesma razão pela qual um canhão tem uma boca.
 */
export const PVP_SUMMON_MUZZLE_MU = PVP_SHIP_RADIUS_MU * 2;
